import { useState } from 'react';
import './App.css';
import logo from './assets/Aizatron logo.png';

function App() {
  const [deviceType, setDeviceType] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [responseData, setResponseData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState('');

  const API_URL = 'https://wloqclclkg.execute-api.af-south-1.amazonaws.com/dev/DeviceRegister';

  const deviceOptions = [
    { value: 'vision', label: 'Vision Controller' },
    { value: 'facekey', label: 'FaceKey' },
    { value: 'cameralogger', label: 'Aizatron Lens' },
    { value: 'sensorlogger', label: 'Aizatron Sense' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      setError('API Key is required');
      return;
    }
    setLoading(true);
    setError(null);
    setResponseData(null);
    setDownloadStatus('');

    const payload = {
      device_type: deviceType,
    };

    try {
      console.log('Sending request to:', API_URL);
      console.log('Payload:', payload);

      const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      console.log('Full response:', data);
      console.log('Response status:', res.status);

      const responseText = JSON.stringify(data).toLowerCase();
      const hasSuccessMessage = responseText.includes('device registered successfully');
      const hasCertificateData = data.certificate_pem || data.private_key || data.public_key ||
        (data.data && (data.data.certificate_pem || data.data.private_key || data.data.public_key));
      const hasDeviceData = data.device_id || data.thing_name ||
        (data.data && (data.data.device_id || data.data.thing_name));

      if (hasSuccessMessage || hasCertificateData || hasDeviceData) {
        console.log('Detected successful response');
        setResponseData(data);

        const certData = data.data || data;
        if (certData.certificate_pem || certData.private_key || certData.public_key) {
          console.log('Certificate data found, attempting downloads...');
          setDownloadStatus('starting');

          setTimeout(() => {
            downloadAllFiles(certData, deviceType);
          }, 300);
        } else {
          console.warn('No certificate data in response');
          setDownloadStatus('no-certificates');
        }
      } else if (data.error || data.message) {
        console.log('Detected error response');
        const errorMessage = data.error || data.message || 'Registration failed';
        throw new Error(errorMessage);
      } else {
        console.log('Unknown response format');
        throw new Error('Unexpected response from server');
      }

    } catch (err) {
      console.error('Request failed:', err);
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (content, filename) => {
    if (!content) {
      console.error('No content to download for:', filename);
      return false;
    }

    console.log(`Attempting to download: ${filename} (length: ${content.length})`);

    try {
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);

      console.log(`Download initiated: ${filename}`);
      return true;

    } catch (err) {
      console.error('Download failed:', err);
      return false;
    }
  };

  const downloadDeviceBundle = (certData, baseFilename) => {
    const bundleContent = (certData.certificate_pem || '') +
                          (certData.private_key || '') +
                          (certData.public_key || '') +
                          (certData.device_friendly_id || '');
    if (bundleContent) {
      return downloadFile(bundleContent, `${baseFilename}_device_bundle.bin`);
    } else {
      console.warn('No data for device bundle');
      return false;
    }
  };

  const downloadAllFiles = (certData, deviceType) => {
    if (!certData) {
      console.error('No certificate data provided for download');
      setDownloadStatus('no-data');
      return;
    }

    console.log('Starting downloads for device:', deviceType);

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const baseFilename = `${deviceType}_${timestamp}`;

    let downloadedCount = 0;

    // Calculate total files to be downloaded
    let totalFiles = 1; // device info file
    if (certData.certificate_pem) totalFiles++;
    if (certData.private_key) totalFiles++;
    if (certData.public_key) totalFiles++;
    // Bundle for ALL devices (5 total)
    totalFiles++;

    // Helper to update final status after all downloads
    const finalizeDownloads = () => {
      if (downloadedCount > 0) {
        setDownloadStatus(`downloaded-${downloadedCount}`);
        console.log(`Successfully initiated download of ${downloadedCount}/${totalFiles} files`);

        setTimeout(() => {
          alert(`✅ ${downloadedCount} file(s) downloaded!\n\nCheck your Downloads folder. If files don't appear, check:\n1. Browser download settings\n2. Pop-up blocker\n3. Try the manual download buttons below`);
        }, 500);
      } else {
        setDownloadStatus('failed');
        console.error('Failed to download any files');
      }
    };

    // 1. Download device info text file (always)
    const deviceInfoContent = `Device ID: ${certData.device_id || ''}\nLicense Key: ${certData.license_key || ''}\nDevice Friendly ID: ${certData.device_friendly_id || ''}`;
    const deviceInfoSuccess = downloadFile(
      deviceInfoContent,
      `${baseFilename}_device_info.txt`
    );
    if (deviceInfoSuccess) downloadedCount++;

    // 2. Download certificates with delays
    setTimeout(() => {
      if (certData.certificate_pem) {
        const success = downloadFile(
          certData.certificate_pem,
          `${baseFilename}_certificate.pem`
        );
        if (success) downloadedCount++;
      } else {
        console.warn('No certificate_pem in certData');
      }

      setTimeout(() => {
        if (certData.private_key) {
          const success = downloadFile(
            certData.private_key,
            `${baseFilename}_private_key.pem`
          );
          if (success) downloadedCount++;
        } else {
          console.warn('No private_key in certData');
        }

        setTimeout(() => {
          if (certData.public_key) {
            const success = downloadFile(
              certData.public_key,
              `${baseFilename}_public_key.pem`
            );
            if (success) downloadedCount++;
          } else {
            console.warn('No public_key in certData');
          }

          // 3. Download the device bundle for ALL devices
          setTimeout(() => {
            const bundleSuccess = downloadDeviceBundle(certData, baseFilename);
            if (bundleSuccess) downloadedCount++;
            finalizeDownloads();
          }, 300);
        }, 300);
      }, 300);
    }, 300);
  };

  const getCertificateData = () => {
    return responseData?.data || responseData || {};
  };

  const handleDownloadCertificate = () => {
    const certData = getCertificateData();
    if (certData.certificate_pem) {
      const filename = `${deviceType}_certificate_${Date.now()}.pem`;
      const success = downloadFile(certData.certificate_pem, filename);
      if (success) {
        alert('Certificate download started!');
      } else {
        alert('Failed to start download. Check console for errors.');
      }
    }
  };

  const handleDownloadPrivateKey = () => {
    const certData = getCertificateData();
    if (certData.private_key) {
      const filename = `${deviceType}_private_key_${Date.now()}.pem`;
      const success = downloadFile(certData.private_key, filename);
      if (success) {
        alert('Private key download started!');
      } else {
        alert('Failed to start download. Check console for errors.');
      }
    }
  };

  const handleDownloadPublicKey = () => {
    const certData = getCertificateData();
    if (certData.public_key) {
      const filename = `${deviceType}_public_key_${Date.now()}.pem`;
      const success = downloadFile(certData.public_key, filename);
      if (success) {
        alert('Public key download started!');
      } else {
        alert('Failed to start download. Check console for errors.');
      }
    }
  };

  const handleDownloadDeviceBundle = () => {
    const certData = getCertificateData();
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const baseFilename = `${deviceType}_${timestamp}`;
    const success = downloadDeviceBundle(certData, baseFilename);
    if (success) {
      alert('Device bundle download started!');
    } else {
      alert('Failed to start download. Check console for errors.');
    }
  };

  const handleDownloadDeviceInfo = () => {
    const certData = getCertificateData();
    if (certData.device_id || certData.license_key || certData.device_friendly_id) {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const baseFilename = `${deviceType}_${timestamp}`;
      const deviceInfoContent = `Device ID: ${certData.device_id || ''}\nLicense Key: ${certData.license_key || ''}\nDevice Friendly ID: ${certData.device_friendly_id || ''}`;
      const success = downloadFile(deviceInfoContent, `${baseFilename}_device_info.txt`);
      if (success) {
        alert('Device info download started!');
      } else {
        alert('Failed to start download. Check console for errors.');
      }
    } else {
      alert('No device info available.');
    }
  };

  const handleDownloadAll = () => {
    const certData = getCertificateData();
    if (certData.certificate_pem || certData.private_key || certData.public_key) {
      downloadAllFiles(certData, deviceType);
    } else {
      alert('No certificates available to download.');
    }
  };

  return (
    <div className="app-container">
      <div className="page-logo">
        <img
          src={logo}
          alt="Aizatron Logo"
          className="logo"
          onError={(e) => {
            console.error('Logo failed to load, showing fallback');
            e.target.style.display = 'none';
            const container = e.target.parentElement;
            if (container && !container.querySelector('.logo-fallback')) {
              const fallback = document.createElement('div');
              fallback.className = 'logo-fallback';
              fallback.textContent = 'AIZATRON';
              container.appendChild(fallback);
            }
          }}
        />
      </div>

      <div className="main-content">
        <div className="container">
          <div className="card">
            <div className="header">
              <h1>Device Registration Portal</h1>
              <p className="subtitle">Secure IoT Device Onboarding Platform</p>
            </div>

            <form onSubmit={handleSubmit} className="form">
              <div className="form-group">
                <label htmlFor="apiKey">API Key</label>
                <input
                  type="password"
                  id="apiKey"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  required
                  className="api-key-input"
                  placeholder="Enter your API key"
                />
              </div>

              <div className="form-group">
                <label htmlFor="deviceType">Select Device</label>
                <select
                  id="deviceType"
                  value={deviceType}
                  onChange={(e) => setDeviceType(e.target.value)}
                  required
                  className="device-select"
                >
                  <option value="" disabled>Choose a device...</option>
                  {deviceOptions.map((device) => (
                    <option key={device.value} value={device.value}>
                      {device.label}
                    </option>
                  ))}
                </select>
              </div>

              <button type="submit" disabled={loading || !deviceType || !apiKey} className={`btn ${loading ? 'btn-loading' : ''}`}>
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Registering Device...
                  </>
                ) : 'Register Device'}
              </button>
            </form>

            {error && (
              <div className="alert alert-error">
                <div className="alert-icon">⚠️</div>
                <div>
                  <strong>Registration Failed</strong>
                  <p>{error}</p>
                </div>
              </div>
            )}

            {responseData && !error && (
              <div className="result">
                <div className="alert alert-success">
                  <div className="alert-icon">✅</div>
                  <div>
                    <strong>Registration Successful</strong>
                    <p>Device registered successfully!</p>
                    {getCertificateData().device_id && (
                      <small>Device ID: {getCertificateData().device_id}</small>
                    )}
                    {downloadStatus === 'starting' && <div className="download-status">Starting downloads...</div>}
                    {downloadStatus.startsWith('downloaded-') && (
                      <div className="download-status">
                        {downloadStatus.replace('downloaded-', '')} file(s) downloading...
                      </div>
                    )}
                  </div>
                </div>

                {getCertificateData().device_id || getCertificateData().thing_name || getCertificateData().license_key ? (
                  <div className="device-info">
                    <h3>Device Details</h3>
                    <div className="info-grid">
                      {getCertificateData().device_id && (
                        <div className="info-item">
                          <span className="info-label">Device ID</span>
                          <span className="info-value">{getCertificateData().device_id}</span>
                        </div>
                      )}
                      {getCertificateData().thing_name && (
                        <div className="info-item">
                          <span className="info-label">Thing Name</span>
                          <span className="info-value">{getCertificateData().thing_name}</span>
                        </div>
                      )}
                      {getCertificateData().license_key && (
                        <div className="info-item">
                          <span className="info-label">License Key</span>
                          <span className="info-value">{getCertificateData().license_key}</span>
                        </div>
                      )}
                      {getCertificateData().device_friendly_id && (
                        <div className="info-item">
                          <span className="info-label">Device Friendly ID</span>
                          <span className="info-value">{getCertificateData().device_friendly_id}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="downloads">
                  <h3>Security Certificates</h3>

                  <p className="download-help">
                    Your security certificates and device bundle have been generated. They will download automatically.
                  </p>

                  <div className="download-buttons">
                    {/* Device Info button for all devices */}
                    {(getCertificateData().device_id || getCertificateData().license_key || getCertificateData().device_friendly_id) && (
                      <button
                        className="btn-download"
                        onClick={handleDownloadDeviceInfo}
                      >
                        <span className="icon">📋</span>
                        Device Info (.txt)
                      </button>
                    )}

                    {getCertificateData().certificate_pem && (
                      <button
                        className="btn-download"
                        onClick={handleDownloadCertificate}
                      >
                        <span className="icon">📄</span>
                        Certificate (.pem)
                      </button>
                    )}

                    {getCertificateData().private_key && (
                      <button
                        className="btn-download"
                        onClick={handleDownloadPrivateKey}
                      >
                        <span className="icon">🔐</span>
                        Private Key (.pem)
                      </button>
                    )}

                    {getCertificateData().public_key && (
                      <button
                        className="btn-download"
                        onClick={handleDownloadPublicKey}
                      >
                        <span className="icon">🔑</span>
                        Public Key (.pem)
                      </button>
                    )}

                    {/* Generic Device Bundle button for ALL devices (grey) */}
                    {(getCertificateData().certificate_pem || getCertificateData().private_key || getCertificateData().public_key) && (
                      <button
                        className="btn-download-grey"
                        onClick={handleDownloadDeviceBundle}
                      >
                        <span className="icon">📦</span>
                        Device Bundle (.bin)
                      </button>
                    )}

                    <button
                      className="btn-download-all"
                      onClick={handleDownloadAll}
                    >
                      <span className="icon">⬇️</span>
                      Download All Files
                    </button>
                  </div>

                  <div className="browser-tips">
                    <h4>Download Troubleshooting</h4>
                    <ul>
                      <li>Check browser's download folder settings</li>
                      <li>Disable pop-up blockers for this site</li>
                      <li>If using Safari, try Chrome or Firefox</li>
                      <li>Open browser console (F12) for debug messages</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          <footer className="footer">
            <p>AIZATRON IoT Platform • Device Registration System</p>
            <p className="footer-note">Secure AWS IoT Core Integration</p>
          </footer>
        </div>
      </div>
    </div>
  );
}

export default App;